import { KnowledgeSource } from '@glow/ai-contract'
import { userLanguageDaOverride } from '@glow/common'
import { IconFa } from '@glow/ui-components'
import i18next from 'i18next'

// A source that predates the app label describes Glow
const GLOW_APP = 'internal-frontend'

const appOf = (source: KnowledgeSource): string => source.app ?? GLOW_APP

// An app the catalog does not name is shown by its own name rather than hidden
const appName = (app: string): string => i18next.t(`aiChat.app.${app}`, { defaultValue: app })

const updatedOn = (source: KnowledgeSource): string =>
  i18next.t('aiChat.sourceUpdated', {
    // The app's language, not the browser's; the helper maps the app's 'dk' to
    // the 'da' Intl expects, without which Danish fell back to the runtime locale
    date: new Date(source.updatedAt).toLocaleDateString(userLanguageDaOverride())
  })

// A source the reader can open is a link; one they cannot - product docs live in a private
// repository - is still named, since where an answer came from matters even without the page
const SourceLine = ({ source }: { source: KnowledgeSource }) =>
  source.url === undefined ? (
    <span className="flex items-baseline gap-1.5 text-xs text-emphasis-high">
      <IconFa icon={['far', 'file-lines']} size="xs" className="text-emphasis-medium" />
      <span>{source.title}</span>
      <span className="text-emphasis-medium">{updatedOn(source)}</span>
    </span>
  ) : (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-baseline gap-1.5 text-xs text-emphasis-high hover:text-primary-500"
    >
      <IconFa icon={['far', 'arrow-up-right-from-square']} size="xs" className="text-emphasis-medium" />
      <span className="underline">{source.title}</span>
      <span className="text-emphasis-medium">{updatedOn(source)}</span>
    </a>
  )

const AppBadge = ({ app }: { app: string }) => (
  <span className="self-start rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold text-emphasis-medium">
    {appName(app)}
  </span>
)

// From what retrieval returned, not links the model wrote - a fabricated citation is worse than
// none. The article's own date is shown because the index is a mirror, so staleness is visible.
// Sources are grouped under the app they describe, so a reader sees at a glance that a statement
// about the customer portal came from its own docs
export const AiSources = ({ sources }: { sources: KnowledgeSource[] }) => {
  if (sources.length === 0) {
    return null
  }
  const apps = [...new Set(sources.map(appOf))]
  return (
    <div className="mt-2 flex flex-col gap-1 border-t border-gray-300 pt-2">
      <span className="text-xs font-semibold text-emphasis-medium">{i18next.t('aiChat.sources')}</span>
      {apps.map((app) => (
        <div key={app} className="flex flex-col gap-1">
          <AppBadge app={app} />
          {sources
            .filter((source) => appOf(source) === app)
            .map((source, index) => (
              <SourceLine key={`${index}|${source.title}`} source={source} />
            ))}
        </div>
      ))}
    </div>
  )
}
